// Fichier: src/pages/accounting/QuickEntry.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, Card, CardActionArea, CardContent,
  Avatar, IconButton, TextField, MenuItem, Button, Switch, FormControlLabel,
  Snackbar, Alert, Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  Storefront as StorefrontIcon,
  TheaterComedy as TheaterComedyIcon,
  Favorite as FavoriteIcon,
  AccountBalance as AccountBalanceIcon,
  ShoppingCart as ShoppingCartIcon,
  Cake as CakeIcon,
  Campaign as CampaignIcon,
  Cloud as CloudIcon,
  MeetingRoom as MeetingRoomIcon,
  Shield as ShieldIcon,
  Checkroom as CheckroomIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs, query, where, setDoc, onSnapshot } from 'firebase/firestore';

// ── Modèles d'opérations courantes ──────────────────────────────────────
// "type: revenue" → Débit = compte de paiement, Crédit = compte contrepartie
// "type: expense" → Débit = compte contrepartie, Crédit = compte de paiement
const TEMPLATES = [
  { id: 'inscription', label: 'Nouvelle inscription', icon: <SchoolIcon />, color: '#0EA5E9', type: 'revenue', counterpartCode: '730', paymentMethods: ['cash', 'virement', 'mollie'] },
  { id: 'goodies', label: 'Vente de goodies', icon: <ShoppingCartIcon />, color: '#8B5CF6', type: 'revenue', counterpartCode: '731', paymentMethods: ['cash', 'virement', 'mollie'] },
  { id: 'prestation', label: 'Prestation artistique', icon: <TheaterComedyIcon />, color: '#EC4899', type: 'revenue', counterpartCode: '732', paymentMethods: ['virement', 'cash'] },
  { id: 'don', label: 'Don reçu', icon: <FavoriteIcon />, color: '#EF4444', type: 'revenue', counterpartCode: '701', paymentMethods: ['virement', 'cash'] },
  { id: 'subside', label: 'Subside reçu', icon: <AccountBalanceIcon />, color: '#22C55E', type: 'revenue', counterpartCode: '703', paymentMethods: ['virement'] },
  { id: 'bureau', label: 'Petit matériel de bureau', icon: <ShoppingCartIcon />, color: '#64748B', type: 'expense', counterpartCode: '600', paymentMethods: ['cash', 'virement'] },
  { id: 'extras', label: 'Petits extras (anniversaires...)', icon: <CakeIcon />, color: '#F97316', type: 'expense', counterpartCode: '640', paymentMethods: ['cash', 'virement'] },
  { id: 'pub', label: 'Publicité réseaux sociaux', icon: <CampaignIcon />, color: '#3B82F6', type: 'expense', counterpartCode: '614', paymentMethods: ['virement', 'cash'] },
  { id: 'workspace', label: 'Domaine et Workspace', icon: <CloudIcon />, color: '#06B6D4', type: 'expense', counterpartCode: '619', paymentMethods: ['virement'] },
  { id: 'location', label: 'Location de salle', icon: <MeetingRoomIcon />, color: '#A855F7', type: 'expense', counterpartCode: '610', paymentMethods: ['virement', 'cash'] },
  { id: 'assurance', label: 'Assurance', icon: <ShieldIcon />, color: '#14B8A6', type: 'expense', counterpartCode: '612', paymentMethods: ['virement'] },
  { id: 'materiel', label: 'Matériel pédagogique / costumes', icon: <CheckroomIcon />, color: '#F59E0B', type: 'expense', counterpartCode: '602', paymentMethods: ['virement', 'cash'] }
];

const PAYMENT_LABELS = { cash: 'Cash', virement: 'Virement', mollie: 'Mollie (montant net)' };
const PAYMENT_ACCOUNT_CODES = { cash: '570', virement: '550', mollie: '550' };

const QuickEntry = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [validateNow, setValidateNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'accounting_accounts'), (snap) => {
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const accountsByCode = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.code] = a; });
    return map;
  }, [accounts]);

  const missingAccounts = useMemo(() => {
    if (!selectedTemplate) return [];
    const codes = [selectedTemplate.counterpartCode, ...selectedTemplate.paymentMethods.map((m) => PAYMENT_ACCOUNT_CODES[m])];
    return codes.filter((c) => !accountsByCode[c]);
  }, [selectedTemplate, accountsByCode]);

  const openTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setAmount('');
    setPaymentMethod(tpl.paymentMethods[0]);
    setDate(new Date().toISOString().slice(0, 10));
    setNote('');
    setValidateNow(false);
  };

  const backToGrid = () => setSelectedTemplate(null);

  const ensureExercice = async (year) => {
    const exId = String(year);
    const exRef = doc(db, 'accounting_exercices', exId);
    const exSnap = await getDoc(exRef);
    if (!exSnap.exists()) {
      await setDoc(exRef, {
        year,
        label: `Exercice ${year}`,
        startDate: new Date(year, 0, 1),
        endDate: new Date(year, 11, 31),
        status: 'open',
        createdAt: new Date()
      });
    }
    return exId;
  };

  const generateReference = async (exerciceId) => {
    const snap = await getDocs(query(collection(db, 'accounting_entries'), where('exerciceId', '==', exerciceId)));
    const n = snap.size + 1;
    return `${exerciceId}-${String(n).padStart(4, '0')}`;
  };

  const handleSave = async () => {
    if (!amount || Number(amount) <= 0) {
      setSnackbar({ open: true, message: 'Indique un montant valide.', severity: 'error' });
      return;
    }
    if (!paymentMethod) {
      setSnackbar({ open: true, message: 'Choisis un mode de paiement.', severity: 'error' });
      return;
    }
    const counterpartAccount = accountsByCode[selectedTemplate.counterpartCode];
    const paymentAccount = accountsByCode[PAYMENT_ACCOUNT_CODES[paymentMethod]];
    if (!counterpartAccount || !paymentAccount) {
      setSnackbar({ open: true, message: 'Un compte nécessaire est introuvable — vérifie le plan comptable.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const dateObj = new Date(date + 'T00:00:00');
      const year = dateObj.getFullYear();
      const exerciceId = await ensureExercice(year);
      const value = Number(amount);

      const description = `${selectedTemplate.label} — ${PAYMENT_LABELS[paymentMethod]}${note ? ` (${note})` : ''}`;

      const lines =
        selectedTemplate.type === 'revenue'
          ? [
              { accountId: paymentAccount.id, accountCode: paymentAccount.code, accountLabel: paymentAccount.label, lineLabel: description, debit: value, credit: 0 },
              { accountId: counterpartAccount.id, accountCode: counterpartAccount.code, accountLabel: counterpartAccount.label, lineLabel: description, debit: 0, credit: value }
            ]
          : [
              { accountId: counterpartAccount.id, accountCode: counterpartAccount.code, accountLabel: counterpartAccount.label, lineLabel: description, debit: value, credit: 0 },
              { accountId: paymentAccount.id, accountCode: paymentAccount.code, accountLabel: paymentAccount.label, lineLabel: description, debit: 0, credit: value }
            ];

      const reference = await generateReference(exerciceId);

      await setDoc(
        doc(collection(db, 'accounting_entries')),
        {
          date: dateObj,
          description,
          exerciceId,
          lines,
          totalDebit: value,
          totalCredit: value,
          status: validateNow ? 'validé' : 'brouillon',
          source: 'saisie_rapide',
          reference,
          createdAt: new Date(),
          updatedAt: new Date()
        },
        { merge: true }
      );

      setSnackbar({
        open: true,
        message: `Écriture ${reference} enregistrée${validateNow ? ' et validée' : ' en brouillon'}.`,
        severity: 'success'
      });
      backToGrid();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Erreur lors de l'enregistrement.", severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => (selectedTemplate ? backToGrid() : navigate('/accounting'))} aria-label="Retour">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Saisie rapide</Typography>
      </Box>

      {!selectedTemplate && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choisis le type d'opération — l'écriture comptable est générée automatiquement.
          </Typography>
          <Grid container spacing={2}>
            {TEMPLATES.map((tpl) => (
              <Grid item xs={6} sm={4} key={tpl.id}>
                <Card sx={{ height: '100%', borderRadius: '12px' }}>
                  <CardActionArea onClick={() => openTemplate(tpl)} sx={{ height: '100%', p: 1 }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <Avatar sx={{ bgcolor: tpl.color, width: 40, height: 40 }}>{tpl.icon}</Avatar>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{tpl.label}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {selectedTemplate && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Avatar sx={{ bgcolor: selectedTemplate.color }}>{selectedTemplate.icon}</Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{selectedTemplate.label}</Typography>
          </Box>

          {missingAccounts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Compte(s) manquant(s) dans le plan comptable : {missingAccounts.join(', ')}. Ajoute-le(s) depuis "Plan comptable" (bouton "Initialiser / compléter avec le plan de base" si ce sont des codes standards).
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Montant (€)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              autoFocus
            />
            <TextField
              select
              label="Mode de paiement"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {selectedTemplate.paymentMethods.map((m) => (
                <MenuItem key={m} value={m}>{PAYMENT_LABELS[m]}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Précision (optionnel)"
              placeholder="ex : nom de l'élève, événement..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <FormControlLabel
              control={<Switch checked={validateNow} onChange={(e) => setValidateNow(e.target.checked)} />}
              label="Valider directement cette écriture (sinon elle reste en brouillon, à relire plus tard)"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Chip
              label={selectedTemplate.type === 'revenue' ? 'Rentrée d\'argent' : 'Dépense'}
              color={selectedTemplate.type === 'revenue' ? 'success' : 'default'}
              size="small"
            />
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              Enregistrer
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QuickEntry;
